import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AccountBalanceWallet,
  AdminPanelSettings,
  ArrowForward,
  Block,
  CheckCircle,
  Groups,
  Public,
  Receipt,
  Refresh,
  Sms,
  Storefront,
  VerifiedUser,
} from '@mui/icons-material';
import { format } from 'date-fns';
import {
  fetchMasterSummary,
  fetchActivityLogs,
  fetchAdminCollectionsSummary,
  fetchAdminPayoutsSummary,
} from '../hooks/hooks';

const ACTION_CHIP_COLOR = {
  login: 'success',
  logout: 'default',
  failed_login: 'error',
  data_change: 'info',
  api_request: 'default',
  admin_action: 'warning',
  db_modification: 'info',
  other: 'default',
};

const formatNum = (val) => Number(val || 0).toLocaleString();
const formatMoney = (val) => Number(val || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const StatCard = ({ icon: Icon, label, value, color, cardBg, borderClr }) => (
  <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
    <CardContent sx={{ py: 1.75, '&:last-child': { pb: 1.75 } }}>
      <Stack direction="row" alignItems="center" gap={1} mb={0.75}>
        <Icon sx={{ fontSize: 18, color: color || '#0BA480' }} />
        <Typography variant="body2" color="text.secondary" noWrap>
          {label}
        </Typography>
      </Stack>
      <Typography variant="h5" fontWeight={700}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const AdminOverview = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const cardBg = isDark ? 'rgba(255,255,255,0.03)' : '#fff';
  const borderClr = isDark ? 'rgba(255,255,255,0.09)' : '#E2E8F0';

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
    refetch: refetchSummary,
  } = useQuery({
    queryKey: ['adminMasterSummary'],
    queryFn: fetchMasterSummary,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const {
    data: collectionsSummary,
    isLoading: collectionsLoading,
    error: collectionsError,
    refetch: refetchCollections,
  } = useQuery({
    queryKey: ['adminCollectionsSummary'],
    queryFn: fetchAdminCollectionsSummary,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const {
    data: payoutsSummary,
    isLoading: payoutsLoading,
    error: payoutsError,
    refetch: refetchPayouts,
  } = useQuery({
    queryKey: ['adminPayoutsSummary'],
    queryFn: fetchAdminPayoutsSummary,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const {
    data: activity,
    isLoading: activityLoading,
    error: activityError,
    refetch: refetchActivity,
  } = useQuery({
    queryKey: ['adminActivityLogs'],
    queryFn: fetchActivityLogs,
    refetchOnWindowFocus: false,
    staleTime: 60 * 1000,
  });

  const activityList = Array.isArray(activity?.results) ? activity.results.slice(0, 20) : [];

  const handleRefreshAll = () => {
    refetchSummary();
    refetchCollections();
    refetchPayouts();
    refetchActivity();
  };

  const statCards = summaryLoading ? [] : [
    { icon: Groups, label: 'Total Users', value: formatNum(summary?.total_users) },
    { icon: CheckCircle, label: 'Active Users', value: formatNum(summary?.active_users), color: '#0BA480' },
    { icon: Block, label: 'Inactive Users', value: formatNum(summary?.inactive_users), color: '#94A3B8' },
    { icon: Storefront, label: 'Business Users', value: formatNum(summary?.business_users) },
    { icon: Groups, label: 'Normal Users', value: formatNum(summary?.normal_users) },
    { icon: VerifiedUser, label: 'Verified Users', value: formatNum(summary?.verified_users), color: '#3B82F6' },
    { icon: Block, label: 'Locked Accounts', value: formatNum(summary?.locked_accounts), color: '#EF4444' },
    { icon: AccountBalanceWallet, label: 'Total Wallet Balance', value: formatMoney(summary?.total_wallet_balance) },
    { icon: AccountBalanceWallet, label: 'Total Float Balance', value: formatMoney(summary?.total_float_balance) },
    { icon: Sms, label: 'Total SMS Balance', value: formatNum(summary?.total_sms_balance) },
  ];

  const moneyCards = (collectionsLoading || payoutsLoading) ? [] : [
    { icon: Receipt, label: 'Total Collections', value: formatMoney(collectionsSummary?.total_collected), color: '#0BA480' },
    { icon: Receipt, label: 'Total Payouts', value: formatMoney(payoutsSummary?.total_payout), color: '#3B82F6' },
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box>
          <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
            <AdminPanelSettings sx={{ color: '#0BA480', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>
              Platform Overview
            </Typography>
            <Chip label="Admin" size="small" color="success" sx={{ fontWeight: 600 }} />
          </Stack>
          <Typography color="text.secondary" fontSize={14}>
            Platform-wide stats, users, and recent activity.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={handleRefreshAll}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </Stack>

      {(summaryError || collectionsError || payoutsError || activityError) && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>
          Failed to load some admin data. Please try refreshing.
        </Alert>
      )}

      {/* Stats */}
      {summaryLoading ? (
        <Typography color="text.secondary" sx={{ mb: 3 }}>Loading platform stats...</Typography>
      ) : (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {statCards.map((s) => (
            <Grid item xs={6} sm={4} md={2.4} key={s.label}>
              <StatCard {...s} cardBg={cardBg} borderClr={borderClr} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Collections + Payouts */}
      {!(collectionsLoading || payoutsLoading) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {moneyCards.map((s) => (
            <Grid item xs={6} sm={3} key={s.label}>
              <StatCard {...s} cardBg={cardBg} borderClr={borderClr} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Gender + Top Countries */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
                Gender Breakdown
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                <Chip label={`Male: ${formatNum(summary?.gender_male)}`} variant="outlined" />
                <Chip label={`Female: ${formatNum(summary?.gender_female)}`} variant="outlined" />
                <Chip label={`Other: ${formatNum(summary?.gender_other)}`} variant="outlined" />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={8}>
          <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px', height: '100%' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" gap={1} mb={1.5}>
                <Public sx={{ fontSize: 18, color: '#0BA480' }} />
                <Typography variant="subtitle2" fontWeight={700}>
                  Top Countries
                </Typography>
              </Stack>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {(summary?.top_countries || []).length === 0 ? (
                  <Typography color="text.secondary" fontSize={13}>No data yet</Typography>
                ) : (
                  summary.top_countries.map((c) => (
                    <Chip key={c.country} label={`${c.country}: ${formatNum(c.count)}`} variant="outlined" />
                  ))
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent activity */}
      <Card elevation={0} sx={{ border: `1px solid ${borderClr}`, bgcolor: cardBg, borderRadius: '12px' }}>
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="subtitle1" fontWeight={700}>
              Recent Platform Activity
            </Typography>
            <Stack direction="row" gap={1.5}>
              <Button component={RouterLink} to="/admin/users" size="small" endIcon={<ArrowForward />} sx={{ color: '#0BA480' }}>
                View all users
              </Button>
              <Button component={RouterLink} to="/admin/activity" size="small" endIcon={<ArrowForward />} sx={{ color: '#0BA480' }}>
                View all activity
              </Button>
            </Stack>
          </Stack>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: isDark ? 'rgba(255,255,255,0.03)' : '#F8FAFC' }}>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Time</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>User</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Action</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: 13 }}>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activityLoading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">Loading activity...</Typography>
                  </TableCell>
                </TableRow>
              ) : activityList.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">No recent activity</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activityList.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <Typography fontSize={12.5}>
                        {log.timestamp ? format(new Date(log.timestamp), 'MMM d, HH:mm') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13}>{log.user || 'System'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={(log.action || 'other').replace('_', ' ')}
                        size="small"
                        color={ACTION_CHIP_COLOR[log.action] || 'default'}
                        sx={{ fontSize: 11, textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13} noWrap sx={{ maxWidth: 420 }}>{log.description}</Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Container>
  );
};

export default AdminOverview;
